/** CablesTab — cable + conduit + tray-fill schedules + tray designer. */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, MagnifyingGlass, FunnelSimple, DownloadSimple } from "@phosphor-icons/react";
import TrayDesigner from "@/components/trays/TrayDesigner";

function SummaryStat({ label, value, testid, accent = false }) {
  return (
    <div className="bg-[#0a0d12] border border-white/10 p-3" data-testid={testid}>
      <div className="label-tag text-[10px]">{label}</div>
      <div className={`font-display text-xl font-bold ${accent ? "text-[#FFCC00]" : "text-[#00E5FF]"}`}>{value}</div>
    </div>
  );
}

export default function CablesTab({ projectId, project, update }) {
  const [code, setCode] = useState(project?.code_standard === "CEC" ? "CEC" : "NEC");
  const [conduitType, setConduitType] = useState("EMT");
  const [conduitTypes, setConduitTypes] = useState(["EMT", "RMC", "IMC", "PVC Sch 40", "PVC Sch 80"]);
  const [media, setMedia] = useState(project?.cable_media || "conduit");
  const [cablesInMedia, setCablesInMedia] = useState(project?.cables_in_media || "");
  const [bundled, setBundled] = useState(true);
  const [material, setMaterial] = useState(project?.conductor_material || "copper");
  const [data, setData] = useState(null);
  const [conduits, setConduits] = useState(null);
  const [busy, setBusy] = useState(false);
  // Cable-schedule filters (client-side, applied after fetch).
  const [filterText, setFilterText] = useState("");
  const [showFailingOnly, setShowFailingOnly] = useState(false);
  // Cable row that's been requested-to-focus (set when a 3D feeder line is
  // clicked). Cleared after we've scrolled+flashed. Reset between projects.
  const [focusTag, setFocusTag] = useState(null);
  const cableRowsRef = useRef({});

  // Subscribe to the "focus a cable" cross-component event. Room3DView fires
  // it from clicking a feeder line in the 3D scene.
  useEffect(() => {
    const handler = (e) => {
      const t = e?.detail?.tag;
      if (t) setFocusTag({ tag: t, ts: e.detail.ts || Date.now() });
    };
    window.addEventListener("eengdes:focus-cable", handler);
    return () => window.removeEventListener("eengdes:focus-cable", handler);
  }, []);

  const firstSync = useRef(true);
  useEffect(() => {
    if (firstSync.current) { firstSync.current = false; return; }
    if (!update) return;
    update({
      cable_media: media,
      cables_in_media: cablesInMedia === "" ? null : Number(cablesInMedia),
      conductor_material: material,
    });
  }, [media, cablesInMedia, material]); // eslint-disable-line

  useEffect(() => {
    api.get("/conduits/options").then(({ data }) => setConduitTypes(data.types)).catch(() => {});
  }, []);

  const load = async (c, ct, m, n, b, mat) => {
    setBusy(true);
    try {
      const params = { code: c, media: m, bundled: b, conductor_material: mat };
      if (n && Number(n) > 0) params.cables_in_media = Number(n);
      const [{ data: cs }, { data: cd }] = await Promise.all([
        api.get(`/projects/${projectId}/cable-schedule`, { params }),
        api.get(`/projects/${projectId}/conduit-schedule`, { params: { code: c, conduit_type: ct } }),
      ]);
      setData(cs); setConduits(cd);
    } catch { toast.error("Failed to compute schedules"); } finally { setBusy(false); }
  };
  // Re-fetch schedules whenever the trays in the project change (delete /
  // rename / reroute in 3D View must invalidate the cached fill/conduit
  // tables). Also re-fetch on the obvious code/media/material switches.
  const traysFingerprint = (project?.cable_trays || [])
    .map((t) => `${t.id}:${t.kind}:${t.id_display || ""}:${(t.points || []).flat().join(",")}`)
    .join("|");
  useEffect(() => {
    load(code, conduitType, media, cablesInMedia, bundled, material);
    /* eslint-disable-next-line */
  }, [projectId, code, conduitType, media, cablesInMedia, bundled, material, traysFingerprint]);

  const MEDIA_LABELS = { free_air: "Free air", tray: "Cable tray", conduit: "Conduit / raceway" };

  // Apply the search-text + failing-only filter on top of the loaded cables.
  // useMemo so we don't recompute on unrelated re-renders.
  const filteredCables = useMemo(() => {
    if (!data?.cables) return [];
    const q = filterText.trim().toLowerCase();
    return data.cables.filter((c) => {
      if (showFailingOnly) {
        const okAmp = c.ampacity_compliant !== false;
        const okVd  = c.vd_compliant !== false;
        if (okAmp && okVd) return false;
      }
      if (!q) return true;
      return [c.tag, c.from, c.to, c.cable_type, c.media]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [data, filterText, showFailingOnly]);

  // Download the current cable schedule as CSV. Uses the loaded `data.cables`
  // (not the filtered view) so users get the full dataset by default.
  const exportCablesCsv = () => {
    if (!data?.cables?.length) {
      toast.message("Nothing to export yet — compute the schedule first.");
      return;
    }
    const cols = [
      ["tag", "Tag"], ["from", "From"], ["to", "To"], ["cable_type", "Type"],
      ["media", "Media"], ["conductor_size", "Size"], ["conductors", "Cnd"],
      ["ccc_in_raceway", "CCC"], ["derating_factor", "Derate"],
      ["derated_ampacity", "Derated A"], ["parallel_sets", "Sets"],
      ["length_ft", "Length ft"], ["load_amps", "Load A"],
      ["voltage_drop_pct", "Vd %"],
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map(([, h]) => h).join(",");
    const rows = data.cables.map((c) =>
      cols.map(([k]) => escape(c[k])).join(","),
    );
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cable-schedule-${projectId || "project"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${data.cables.length} cable rows`);
  };

  // After the schedule loads (or focusTag changes), scroll the requested
  // cable row into view + add a brief yellow flash so the user sees what was
  // clicked from the 3D scene. Cleared automatically after ~2.5s.
  useEffect(() => {
    if (!focusTag || !data) return;
    const tag = focusTag.tag;
    // Match either the exact cable.tag (e.g. "VFD-101") or the prefixed
    // "C-VFD-101" form produced by the schedule builder. Indexed against
    // the filtered list since that's what we actually render.
    const idx = filteredCables.findIndex(
      (c) => c.tag === tag || c.tag === `C-${tag}` || c.to === tag,
    );
    if (idx < 0) {
      // Row exists in raw data but filter is hiding it — clear filters so
      // the user can see it.
      const inRaw = (data.cables || []).find(
        (c) => c.tag === tag || c.tag === `C-${tag}` || c.to === tag,
      );
      if (inRaw && (filterText || showFailingOnly)) {
        setFilterText("");
        setShowFailingOnly(false);
        toast.message(`Filters cleared to reveal ${tag}`);
        return;
      }
      toast.message(`No cable row found for ${tag}`);
      setFocusTag(null);
      return;
    }
    const el = cableRowsRef.current[idx];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("cable-row-flash");
      const t = setTimeout(() => {
        el.classList.remove("cable-row-flash");
        setFocusTag(null);
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [focusTag, data, filteredCables, filterText, showFailingOnly]);

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tag">// 05 Cable & conduit schedules</div>
          <h2 className="font-display text-2xl font-bold">Cables, trays & raceways</h2>
          <p className="text-sm text-gray-400 mt-1">Auto-derived from circuits + positioned equipment list. Cable type defaults: THWN-2 (NEC) / RW90 (CEC).</p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <div className="label-tag mb-1">Code</div>
            <div className="inline-flex border border-white/10">
              {["NEC", "CEC"].map((c) => (
                <button key={c} onClick={() => setCode(c)} className={`px-3 py-2 text-sm font-mono ${code === c ? "bg-[#00E5FF] text-black" : "text-gray-400 hover:text-white"}`} data-testid={`cables-code-${c}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <div className={`label-tag mb-1 ${media !== "conduit" ? "opacity-40" : ""}`}>
              Conduit type {media !== "conduit" && <span className="normal-case text-[9px] text-gray-500">(media must be conduit)</span>}
            </div>
            <select value={conduitType} onChange={(e) => setConduitType(e.target.value)}
                    disabled={media !== "conduit"}
                    className={`bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none ${
                      media !== "conduit" ? "opacity-40 cursor-not-allowed" : ""
                    }`}
                    data-testid="conduit-type-select">
              {conduitTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <div className="label-tag mb-1">Conductor material</div>
            <select value={material} onChange={(e) => setMaterial(e.target.value)}
                    className="bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="conductor-material-select">
              <option value="copper">Copper (Cu)</option>
              <option value="aluminum">Aluminum (Al)</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4 bg-[#0a0d12] border border-white/10 p-4">
        <div>
          <div className="label-tag mb-1">Media</div>
          <select value={media} onChange={(e) => setMedia(e.target.value)} className="input-base text-sm w-full" data-testid="cables-media-select">
            <option value="free_air">{MEDIA_LABELS.free_air}</option>
            <option value="tray">{MEDIA_LABELS.tray}</option>
            <option value="conduit">{MEDIA_LABELS.conduit}</option>
          </select>
        </div>
        <div>
          <div className="label-tag mb-1">Cables / wires in media</div>
          <input type="number" min="0" step="1" placeholder="auto" value={cablesInMedia} onChange={(e) => setCablesInMedia(e.target.value)} className="input-base text-sm w-full" data-testid="cables-in-media-input" />
          <div className="text-[10px] text-gray-500 font-mono mt-1">Override the auto-grouped count</div>
        </div>
        <div>
          <div className="label-tag mb-1">Arrangement</div>
          <select value={bundled ? "bundled" : "spaced"} onChange={(e) => setBundled(e.target.value === "bundled")} className="input-base text-sm w-full" disabled={media === "conduit"} data-testid="cables-arrangement-select" title={media === "conduit" ? "Conduit always treated as bundled per 310.15(C)(1)" : ""}>
            <option value="bundled">Bundled / stacked</option>
            <option value="spaced">Spaced ≥ 1 OD</option>
          </select>
        </div>
        <div className="flex items-end">
          <button onClick={() => load(code, conduitType, media, cablesInMedia, bundled, material)} className="btn-ghost text-sm py-2 inline-flex items-center gap-2 w-full justify-center" data-testid="cables-recompute" disabled={busy}>
            <ArrowsClockwise size={14} /> Recompute
          </button>
        </div>
      </div>

      {data?.derating_summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4" data-testid="derating-summary">
          <SummaryStat label="Media"             value={MEDIA_LABELS[data.derating_summary.media] || data.derating_summary.media} testid="ds-media" />
          <SummaryStat label="Cables in media"   value={data.derating_summary.cables_in_media}                                     testid="ds-count" />
          <SummaryStat label="CCC total"          value={data.derating_summary.ccc_total}                                           testid="ds-ccc" />
          <SummaryStat label="Derating factor"    value={(data.derating_summary.derating_factor ?? 1).toFixed(2)}                  testid="ds-factor" accent />
          <SummaryStat label="Arrangement"        value={data.derating_summary.bundled ? "Bundled" : "Spaced"}                     testid="ds-arrangement" />
        </div>
      )}
      {data?.derating_summary?.citation && (
        <div className="border border-white/10 bg-[#0a0d12] p-3 mb-4 text-xs font-mono text-gray-300" data-testid="derating-citation">
          <span className="text-gray-500">// applied rule:</span> {data.derating_summary.citation}
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-xs font-mono text-[#00E5FF] mb-3"
             data-testid="cables-loading">
          <ArrowsClockwise size={14} className="animate-spin" />
          Computing schedules…
        </div>
      )}

      <div className="mb-6">
        <TrayDesigner project={project} projectId={projectId} update={update} />
      </div>

      {data && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
            <div className="label-tag">// Cable schedule</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <MagnifyingGlass size={12}
                                 className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search tag, from, to…"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="bg-black/40 border border-white/10 pl-7 pr-2 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none w-48"
                  data-testid="cable-filter-input"
                />
              </div>
              <button
                onClick={() => setShowFailingOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-mono border ${
                  showFailingOnly
                    ? "border-[#FF8A85] bg-[#FF8A85]/15 text-[#FF8A85]"
                    : "border-white/10 text-gray-400 hover:text-white hover:border-white/30"
                }`}
                data-testid="cable-failing-toggle"
                title="Show only cables that fail ampacity or voltage-drop"
              >
                <FunnelSimple size={12} />
                {showFailingOnly ? "Failing only" : "All rows"}
              </button>
              <button
                onClick={exportCablesCsv}
                className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-mono border border-white/10 text-gray-300 hover:text-[#00E5FF] hover:border-[#00E5FF]/40"
                data-testid="cable-csv-export"
                title="Download the full cable schedule as CSV"
              >
                <DownloadSimple size={12} /> CSV
              </button>
              <span className="text-[10px] text-gray-500 font-mono">
                {filteredCables.length} / {data.cables.length} rows
              </span>
            </div>
          </div>
          <div className="border border-white/10 overflow-x-auto max-h-[640px] overflow-y-auto" data-testid="cable-schedule-table">
            <table className="w-full">
              <thead className="bg-[#1A1F29] sticky top-0 z-10">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                  <th className="p-3">Tag</th><th className="p-3">From</th><th className="p-3">To</th><th className="p-3">Type</th>
                  <th className="p-3">Media</th><th className="p-3">Size</th><th className="p-3 text-right">Cnd</th>
                  <th className="p-3 text-right" title="Current-carrying conductors in raceway">CCC</th>
                  <th className="p-3 text-right" title="Derating factor (NEC 310.15(C)(1) / 392.80(A) / CEC 4-004(11))">Derate</th>
                  <th className="p-3 text-right">Derated A</th><th className="p-3 text-right">Sets</th>
                  <th className="p-3 text-right">Length ft</th><th className="p-3 text-right">Load A</th>
                  <th className="p-3 text-right">Vd %</th><th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCables.map((c, i) => {
                  const okAmp = c.ampacity_compliant !== false;
                  const okVd  = c.vd_compliant !== false;
                  const failing = !okAmp || !okVd;
                  return (
                    <tr key={i}
                        ref={(el) => { cableRowsRef.current[i] = el; }}
                        className={`border-t border-white/5 font-mono text-sm transition-colors ${
                          failing ? "bg-[#FF8A85]/[0.04] hover:bg-[#FF8A85]/10" : "hover:bg-white/[0.03]"
                        }`}
                        data-testid={`cable-row-${i}`}
                        data-cable-tag={c.tag}>
                      <td className="p-3 text-[#00E5FF]">{c.tag}</td><td className="p-3">{c.from}</td><td className="p-3">{c.to}</td>
                      <td className="p-3">{c.cable_type}</td><td className="p-3 text-gray-400">{c.media || "—"}</td>
                      <td className="p-3">{c.conductor_size}</td><td className="p-3 text-right">{c.conductors}</td>
                      <td className="p-3 text-right">{c.ccc_in_raceway ?? c.ccc ?? "—"}</td>
                      <td className={`p-3 text-right ${c.derating_factor < 1 ? "text-[#FFCC00]" : "text-gray-400"}`}>{(c.derating_factor ?? 1).toFixed(2)}</td>
                      <td className="p-3 text-right">{c.derated_ampacity ?? "—"}</td>
                      <td className="p-3 text-right">{c.parallel_sets}</td><td className="p-3 text-right">{c.length_ft}</td>
                      <td className="p-3 text-right">{c.load_amps}</td>
                      <td className={`p-3 text-right ${okVd ? "text-gray-300" : "text-[#FF8A85]"}`}>{c.voltage_drop_pct ?? "—"}</td>
                      <td className="p-3">
                        {okAmp && okVd ? <span className="text-[#00E5FF]">✓</span> : <span className="text-[#FF8A85]">✗ {!okAmp ? "AMP " : ""}{!okVd ? "VD" : ""}</span>}
                      </td>
                    </tr>
                  );
                })}
                {filteredCables.length === 0 && (
                  <tr><td colSpan={15} className="p-6 text-center text-gray-500 text-sm">
                    {data.cables.length === 0
                      ? "No cables — add circuits or equipment-list items."
                      : "No rows match the current filter."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-xs text-gray-500 font-mono">
            <strong className="text-gray-300">Vd limit:</strong> {data.vd_limit_pct ?? 3}% feeder ·
            <strong className="text-gray-300 ml-2">Derating:</strong> per CCC in shared raceway
          </div>

          <div className="mt-6">
            <div className="label-tag mb-2">
              // Tray fill — {data.tray_fill.tray_width_in}&quot;×{data.tray_fill.tray_height_in}&quot; · CSA {data.tray_fill.tray_csa_sq_in} in² · limit {data.tray_fill.fill_limit_pct}%
            </div>
            <div className="space-y-2" data-testid="tray-fill-bars">
              {(() => {
                // Hide fill bars for trays the user has deleted but not yet
                // saved — keeps the panel in sync with the live 3D state.
                const liveIds = new Set((project?.cable_trays || []).map((t) => t.id));
                const segs = (data.tray_fill.segments || []).filter((s) => !s.id || liveIds.has(s.id));
                return segs.map((s, i) => {
                  const pct = Math.min(100, s.fill_pct);
                  const over = !s.compliant;
                  // Use live id_display if available (rename in 3D should reflect here).
                  const t = (project?.cable_trays || []).find((x) => x.id === s.id);
                  const labelTag = t?.id_display || `Seg ${String(i + 1).padStart(2, "0")}`;
                  return (
                    <div key={s.id || i} className="border border-white/10 p-3">
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="text-gray-300">{labelTag} · {s.kind} · {(s.length_in / 12).toFixed(1)} ft</span>
                        <span className={over ? "text-[#FF8A85]" : "text-[#00E5FF]"}>{s.fill_pct}% {over ? "· OVER LIMIT" : ""}</span>
                      </div>
                      <div className="mt-2 h-2 bg-white/10">
                        <div className={`h-full ${over ? "bg-[#FF8A85]" : "bg-[#00E5FF]"}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                });
              })()}
              {((data.tray_fill.segments || []).filter((s) => {
                const liveIds = new Set((project?.cable_trays || []).map((t) => t.id));
                return !s.id || liveIds.has(s.id);
              }).length === 0) && (
                <div className="text-gray-500 text-sm font-mono">No tray segments. Auto-route or draw trays on the Size tab.</div>
              )}
            </div>
          </div>

          {conduits && media === "conduit" && (conduits.conduits?.length || 0) > 0 && (
            <div className="mt-8">
              <div className="label-tag mb-2">// Conduit schedule ({conduitType}) — {code}</div>
              <div className="border border-white/10 overflow-x-auto" data-testid="conduit-schedule-table">
                <table className="w-full">
                  <thead className="bg-[#1A1F29]">
                    <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-mono text-left">
                      <th className="p-3">Tag</th><th className="p-3">Type</th><th className="p-3">Trade</th>
                      <th className="p-3 text-right">Cables</th><th className="p-3">Cable tags</th>
                      <th className="p-3 text-right">Fill in²</th><th className="p-3 text-right">CSA in²</th>
                      <th className="p-3 text-right">Fill %</th><th className="p-3 text-right">Limit %</th>
                      <th className="p-3 text-right">Length ft</th><th className="p-3">OK?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conduits.conduits.map((c, i) => (
                      <tr key={c.tag || `conduit-${i}`} className="border-t border-white/5 font-mono text-sm">
                        <td className="p-3 text-[#00E5FF]">{c.tag}</td>
                        <td className="p-3">{c.conduit_type}</td>
                        <td className="p-3">{c.trade_size}</td>
                        <td className="p-3 text-right">{c.cable_count}</td>
                        <td className="p-3 text-gray-400 truncate max-w-[180px]">{(c.cable_tags || []).join(", ")}</td>
                        <td className="p-3 text-right">{c.fill_in2}</td>
                        <td className="p-3 text-right">{c.csa_in2 ?? "—"}</td>
                        <td className="p-3 text-right">{c.fill_pct ?? "—"}</td>
                        <td className="p-3 text-right">{c.fill_limit}</td>
                        <td className="p-3 text-right">{c.length_ft}</td>
                        <td className={`p-3 ${c.compliant ? "text-[#00E5FF]" : "text-[#FF8A85]"}`}>{c.compliant ? "✓" : "✗ OVER"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 text-xs text-gray-500 font-mono"><strong className="text-gray-300">Ref:</strong> {conduits.reference}</div>
            </div>
          )}

          <div className="mt-6 text-xs text-gray-500 font-mono"><strong className="text-gray-300">Cable ref:</strong> {data.reference}</div>
        </>
      )}

      {/* Per-tray conduit overrides — lets users opt-in horizontal-tray
          conduits (the v1.56 ConduitLayer renders cylinders for runs only
          when `conduit_type` is set). Auto-tag via AI is still available
          from the 3D right-click menu. */}
      <PerTrayConduitPicker project={project} update={update} />
    </div>
  );
}

const CONDUIT_OPTIONS = ["", "EMT", "RMC", "IMC", "PVC", "FMC", "ENT", "LFMC"];

function PerTrayConduitPicker({ project, update }) {
  const runs = (project?.cable_trays || []).filter((t) => t.kind === "run");
  if (runs.length === 0) return null;
  const setConduit = (id, ct) => {
    const next = (project.cable_trays || []).map((t) =>
      t.id === id ? { ...t, conduit_type: ct || undefined } : t,
    );
    update({ cable_trays: next });
    toast.success(ct
      ? `${ct} applied to tray ${id.slice(0, 6)}`
      : `Conduit cleared on tray ${id.slice(0, 6)}`);
  };
  return (
    <div className="mt-8 border border-[#FFCC00]/30 bg-[#0a0d12] p-4"
         data-testid="cables-tray-conduit-picker">
      <div className="label-tag mb-2">// Tray conduit overrides ({runs.length} run{runs.length === 1 ? "" : "s"})</div>
      <div className="text-xs text-gray-400 mb-3">
        Set a conduit type on any horizontal tray run to enable a horizontal
        conduit cylinder in the 3D view (alongside the cable-tray ladder).
        Right-click a run in 3D view for an AI suggestion.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="text-left text-gray-500 border-b border-white/10">
              <th className="p-2">Tray ID</th>
              <th className="p-2">Elev (in)</th>
              <th className="p-2">Wall</th>
              <th className="p-2">Width × Height</th>
              <th className="p-2">Conduit type</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((t) => (
              <tr key={t.id} className="border-b border-white/5"
                  data-testid={`cables-tray-row-${t.id_display || t.id.slice(0, 6)}`}>
                <td className="p-2 text-[#FFCC00]">{t.id_display || t.id.slice(0, 6)}</td>
                <td className="p-2 text-gray-300">{t.elevation_in ?? "—"}</td>
                <td className="p-2 text-gray-400">{t.wall || "—"}</td>
                <td className="p-2 text-gray-400">{t.width_in || 12}×{t.height_in || 4}</td>
                <td className="p-2">
                  <select value={t.conduit_type || ""}
                          onChange={(e) => setConduit(t.id, e.target.value)}
                          className="bg-black/40 border border-white/10 px-2 py-1 focus:border-[#FFCC00] outline-none"
                          data-testid={`cables-tray-conduit-${t.id_display || t.id.slice(0, 6)}`}>
                    {CONDUIT_OPTIONS.map((opt) => (
                      <option key={opt || "none"} value={opt}>
                        {opt || "— none —"}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
